import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import type { LiveSeoContent as LiveSeoPageData } from "~/data/liveSeoContent.functions";
import styles from "../live-seo-page.module.css";

type WhyIBecameOrthodontistLocalization = {
	h1: string;
	date: string;
	byline: string;
	paragraph1: string;
	paragraph2: string;
	paragraph3: string;
	relatedTitle: string;
	relatedHalloweenDate: string;
	relatedHalloweenTitle: string;
	relatedMissionDate: string;
	relatedMissionTitle: string;
};

const whyIBecameOrthodontistLocalization = {
	[Locale.EN]: {
		h1: "Why did you become an orthodontist?",
		date: "November 23, 2016",
		byline: "By: Dr. Carmen Garcia-Paul, DMD, MS",
		paragraph1:
			"Last week, I went to an elementary school to speak to 2nd, 3rd, 4th and 5th graders about braces and brushing their teeth. I love giving these presentations because the kids get so into it. After showing them a video of what it looks like when you get your braces put on, many of them told me that they felt “completely relieved” and “not terrified anymore.” I think that they thought that the braces had to be drilled into the teeth, but by the end of the video they were excited rather than scared of the process.",
		paragraph2:
			"After the presentation, I always leave time for questions because kids always seem to want to know more about braces and flossing. In the 2nd grade class, I had to go one by one because every single child had a question. One of the 4th graders asked me, “What made you decide to become an orthodontist?” That question made me pause for a second. I had to think back to over 13 years ago when I first started this journey, but my answer is the same now as it was when I was asked in my dental school interview at UPenn. I decided to become an orthodontist because I wanted to help people. Every day, I get to help create beautiful smiles. Some patients come in because they’re being teased at school, some because their parents say that they won’t smile in pictures, and some because they want to have straight teeth for their quinces or wedding pictures… and I can help them all. It’s a very rewarding feeling. The great thing about orthodontics, is that I get to see my patients every 6-8 weeks. I get to see them grow up, hear about their accomplishments, and they become like family.",
		paragraph3:
			"The 4th graders wrote me thank you letters for speaking to their class. I had to share a couple because they made me smile…",
		relatedTitle: "Also Worth Checking Out",
		relatedHalloweenDate: "Oct 31, 2016",
		relatedHalloweenTitle: "Happy Halloween!",
		relatedMissionDate: "Jan 22, 2016",
		relatedMissionTitle: "Our Mission",
	},
	[Locale.ES]: {
		h1: "¿Por qué te convertiste en ortodoncista?",
		date: "23 de noviembre de 2016",
		byline: "Por: Dra. Carmen Garcia-Paul, DMD, MS",
		paragraph1:
			"La semana pasada fui a una escuela primaria para hablar con estudiantes de 2.º, 3.º, 4.º y 5.º grado sobre los brackets y cómo cepillarse los dientes. Me encanta dar estas presentaciones porque los niños se involucran muchísimo. Después de mostrarles un video de cómo es cuando te colocan los brackets, muchos me dijeron que se sintieron “completamente aliviados” y “ya no aterrados”. Creo que pensaban que los brackets tenían que perforarse en los dientes, pero al final del video estaban emocionados en lugar de asustados por el proceso.",
		paragraph2:
			"Después de la presentación, siempre dejo tiempo para preguntas porque los niños parecen querer saber más sobre los brackets y el uso del hilo dental. En la clase de 2.º grado tuve que ir uno por uno porque cada niño tenía una pregunta. Una estudiante de 4.º grado me preguntó: “¿Qué te hizo decidir convertirte en ortodoncista?” Esa pregunta me hizo detenerme un segundo. Tuve que pensar en hace más de 13 años, cuando empecé este camino, pero mi respuesta es la misma ahora que cuando me lo preguntaron en mi entrevista de la escuela dental en UPenn. Decidí convertirme en ortodoncista porque quería ayudar a las personas. Todos los días puedo ayudar a crear sonrisas hermosas. Algunos pacientes vienen porque se burlan de ellos en la escuela, otros porque sus padres dicen que no sonríen en las fotos, y otros porque quieren tener dientes derechos para sus quinceañeras o fotos de boda… y puedo ayudarlos a todos. Es una sensación muy gratificante. Lo mejor de la ortodoncia es que veo a mis pacientes cada 6 a 8 semanas. Los veo crecer, escucho sobre sus logros y se vuelven como familia.",
		paragraph3:
			"Los estudiantes de 4.º grado me escribieron cartas de agradecimiento por hablar con su clase. Tenía que compartir un par porque me hicieron sonreír…",
		relatedTitle: "También vale la pena leer",
		relatedHalloweenDate: "31 de octubre de 2016",
		relatedHalloweenTitle: "¡Feliz Halloween!",
		relatedMissionDate: "22 de enero de 2016",
		relatedMissionTitle: "Nuestra misión",
	},
} satisfies Record<Locale, WhyIBecameOrthodontistLocalization>;

export function WhyIBecameOrthodontistPage({
	page,
}: {
	page: LiveSeoPageData;
}) {
	const getLocalization = useLocalization(whyIBecameOrthodontistLocalization);

	return (
		<Flex direction="column">
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/miami-ortho-13.jpg"
						alt="Smiling children with braces and an orthodontist in Miami, surrounded by greenery, promoting Miami Orthodontic Specialists' family-friendly care."
						width={page.image?.width}
						height={page.image?.height}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 760px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{getLocalization("h1")}
					</Heading>
					<Text as="p" className={styles.postDate}>
						{getLocalization("date")}
					</Text>
					<Box className={styles.entryContent}>
						<Text as="p" size="4">
							<strong>{getLocalization("byline")}</strong>
						</Text>
						<Text as="p" size="4">
							{getLocalization("paragraph1")}
						</Text>
						<Text as="p" size="4">
							{getLocalization("paragraph2")}
						</Text>
						<Text as="p" size="4">
							{getLocalization("paragraph3")}
						</Text>
						<OptimizedImage
							src="/letters-to-dr-garcia-paul.jpg"
							alt="letters-to-dr-garcia-paul"
							width={825}
							height={525}
							className={styles.inlinePostImage}
						/>
					</Box>
					<section className={styles.relatedPosts}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("relatedTitle")}
						</Heading>
						<Grid columns={{ initial: "1", sm: "2" }} gap="5">
							<Link to="/happy-halloween" className={styles.relatedCard}>
								<OptimizedImage
									src="/miami-ortho-4.jpg"
									alt="Children engaging with interactive tablets in a welcoming orthodontic office, showcasing Miami Orthodontic Specialists' family-friendly environment and patient-centered care."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedHalloweenDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedHalloweenTitle")}
									</Heading>
								</Box>
							</Link>
							<Link
								to="/welcome-miami-orthodontics"
								className={styles.relatedCard}
							>
								<OptimizedImage
									src="/same-day.jpg"
									alt="Young girl in orthodontic chair, smiling and giving a thumbs up, showcasing braces in Miami orthodontic office with dental equipment and x-ray monitor in background."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedMissionDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedMissionTitle")}
									</Heading>
								</Box>
							</Link>
						</Grid>
					</section>
				</Box>
			</article>
		</Flex>
	);
}
